import React, { useState } from "react";
import { ArrowRight, Phone, Mail, MapPin, Instagram, Facebook, Heart, Flower2, Sparkles, Scale } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";
import { CONTACT, SOCIAL, IMAGES } from "../data/site";
import FlowerDivider from "../components/FlowerDivider";

const Section = ({ id, children, className = "" }) => (
  <section id={id} className={`relative z-10 scroll-mt-24 ${className}`}>{children}</section>
);

export default function Home() {
  const { t } = useLang();
  const [sent, setSent] = useState(false);

  // Original had 2 offer cards: Lomi Lomi Nui + 6 Tibeter
  const cards = [
    { img: IMAGES.offerLomi, title: t.offer.lomiTitle, desc: t.offer.lomiDesc, href: "#lomi", cls: "gold" },
    { img: IMAGES.offerTibeter, title: t.offer.tibTitle, desc: t.offer.tibDesc, href: "#tibeter", cls: "brown" },
  ];

  return (
    <main data-testid="home-page">
      {/* HERO — dark full-bleed Buddha photo with text overlay */}
      <Section id="top" className="relative min-h-[100svh] overflow-hidden bg-[#0e0b07]">
        <div className="absolute inset-0">
          <img
            src={IMAGES.heroBuddha}
            alt="Buddha – Lotus Sutra"
            data-testid="hero-buddha-img"
            className="w-full h-full object-cover object-[70%_center] md:object-[70%_top]"
          />
          <div className="absolute inset-0 hidden md:block"
               style={{ background: "linear-gradient(90deg, rgba(12,9,6,0.85) 0%, rgba(12,9,6,0.55) 35%, rgba(12,9,6,0) 60%)" }} />
          <div className="absolute inset-0 md:hidden"
               style={{ background: "linear-gradient(180deg, rgba(12,9,6,0.3) 0%, rgba(12,9,6,0.55) 45%, rgba(12,9,6,0.85) 100%)" }} />
        </div>

        <div className="container-x relative z-10 min-h-[100svh] flex items-end md:items-center">
          <div className="w-full md:max-w-xl pt-28 pb-14 md:pb-24 float-in text-center md:text-left">
            <h1 className="font-serif italic text-white text-[64px] sm:text-[88px] md:text-[140px] leading-[0.9] tracking-tight">
              Lotus<br />Sutra
            </h1>
            <p className="mt-4 md:mt-5 text-white/90 tracking-[0.45em] uppercase text-xs sm:text-sm md:text-base" data-testid="hero-kicker">
              Lomi Lomi Nui
            </p>
            <p className="mt-5 text-white/95 font-serif italic text-base sm:text-lg md:text-xl max-w-md mx-auto md:mx-0" data-testid="hero-subtitle">
              {t.hero.subtitle}
            </p>
            <div className="mt-7 flex flex-wrap gap-3 justify-center md:justify-start">
              <a href="#contact"
                 className="inline-flex items-center gap-2 px-7 py-3.5 md:px-9 md:py-4 rounded-full bg-[var(--gold)] text-[var(--ink)] font-medium hover:bg-[var(--gold-light)] transition-all shadow-[0_14px_30px_-12px_rgba(201,162,74,.8)]"
                 data-testid="hero-book-btn">
                {t.hero.cta}
              </a>
              <a href="#about"
                 className="inline-flex items-center gap-2 px-7 py-3.5 md:px-9 md:py-4 rounded-full border border-white/80 text-white hover:bg-white hover:text-[var(--ink)] transition-all"
                 data-testid="hero-more-btn">
                {t.hero.cta2}
              </a>
            </div>
          </div>
        </div>
      </Section>

      {/* ABOUT */}
      <Section id="about" className="py-16 md:py-24">
        <div className="container-x grid md:grid-cols-12 gap-8 md:gap-12 items-center">
          <div className="md:col-span-6 order-2 md:order-1">
            <h2 className="display text-4xl sm:text-5xl md:text-6xl mb-2">{t.about.label}</h2>
            <p className="text-xl md:text-2xl font-serif italic text-[var(--gold)] mb-6 md:mb-8" data-testid="about-name">{t.about.name}</p>
            <div className="space-y-4 md:space-y-5 text-[16px] md:text-[17px] leading-relaxed text-[var(--ink-soft)] max-w-xl">
              <p>{t.about.p1}</p>
              <p>{t.about.p2}</p>
              <p className="font-serif italic text-[var(--ink)] text-lg md:text-xl">{t.about.p3}</p>
            </div>
          </div>
          <div className="md:col-span-6 order-1 md:order-2">
            <div className="img-mask aspect-[4/5] rounded-3xl">
              <img src={IMAGES.aboutPortrait} alt={t.about.name} data-testid="about-portrait" />
            </div>
          </div>
        </div>
      </Section>

      {/* OFFERING — Only 2 cards (original layout) */}
      <Section id="offer" className="py-16 md:py-24 bg-[var(--cream-soft)]">
        <div className="container-x text-center">
          <h2 className="display text-4xl sm:text-5xl md:text-6xl">{t.offer.label}</h2>
          <FlowerDivider />
        </div>
        <div className="container-x grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6 mt-4 max-w-5xl mx-auto">
          {cards.map((c) => (
            <a key={c.title} href={c.href} className={`offer-card ${c.cls}`} data-testid={`offer-card-${c.href.replace('#','')}`}>
              <div className="inner aspect-[4/3]"><img src={c.img} alt={c.title} /></div>
              <div className="pt-5 pb-3 px-2 text-center">
                <div className="font-serif text-2xl md:text-3xl">{c.title}</div>
                <p className="text-sm opacity-90 mt-1">{c.desc}</p>
              </div>
            </a>
          ))}
        </div>
      </Section>

      {/* LOMI LOMI NUI — Tempelritual mit Ölen */}
      <Section id="lomi" className="py-16 md:py-24">
        <div className="container-x text-center">
          <p className="uppercase tracking-[0.3em] text-xs text-[var(--gold)]">{t.lomi.label}</p>
          <h2 className="display text-4xl sm:text-5xl md:text-7xl mt-3">{t.lomi.title}</h2>
          <FlowerDivider />
          <p className="font-serif italic text-lg md:text-2xl text-[var(--gold-dark)] max-w-2xl mx-auto">{t.lomi.sub}</p>
        </div>

        <div className="container-x max-w-3xl mt-10 md:mt-12 space-y-4 md:space-y-5 text-[16px] md:text-[17px] leading-relaxed text-[var(--ink-soft)]">
          <p>{t.lomi.p1}</p>
          <p>{t.lomi.p2}</p>
          <p>{t.lomi.p3}</p>
          <p className="font-serif italic text-[var(--ink)] text-lg md:text-xl border-l-4 border-[var(--gold)] pl-4 md:pl-5 py-1">{t.lomi.p4}</p>
        </div>

        <div className="container-x grid grid-cols-1 sm:grid-cols-3 gap-4 md:gap-5 mt-10 md:mt-14">
          {[IMAGES.lomiOil1, IMAGES.lomiOil2, IMAGES.lomiOil3].map((src, i) => (
            <div key={i} className="img-mask aspect-[4/5] rounded-2xl" data-testid={`lomi-oil-${i+1}`}>
              <img src={src} alt={`Tempelritual mit Ölen ${i+1}`} />
            </div>
          ))}
        </div>
      </Section>

      {/* 6 TIBETER */}
      <Section id="tibeter" className="py-16 md:py-24 bg-[var(--cream-soft)]">
        <div className="container-x text-center">
          <p className="uppercase tracking-[0.3em] text-xs text-[var(--gold)]">{t.tibeter.label}</p>
          <h2 className="display text-4xl sm:text-5xl md:text-7xl mt-3">{t.tibeter.title}</h2>
          <FlowerDivider />
          <p className="font-serif italic text-lg md:text-2xl text-[var(--gold-dark)] max-w-2xl mx-auto">{t.tibeter.sub}</p>
        </div>

        <div className="container-x grid md:grid-cols-12 gap-8 md:gap-10 mt-10 md:mt-14 items-start">
          <div className="md:col-span-5">
            <div className="img-mask aspect-[4/5] rounded-3xl"><img src={IMAGES.offerTibeter} alt={t.tibeter.title} /></div>
          </div>
          <div className="md:col-span-7 space-y-4 md:space-y-5 text-[16px] md:text-[17px] leading-relaxed text-[var(--ink-soft)]">
            <p>{t.tibeter.p1}</p>
            <p>{t.tibeter.p2}</p>
            <div className="bg-[var(--gold)]/10 rounded-3xl p-6 md:p-7 border border-[var(--gold)]/25 mt-5">
              <div className="font-serif text-xl md:text-2xl mb-4">{t.tibeter.effectsTitle}</div>
              <ul className="space-y-2.5 md:space-y-3">
                {t.tibeter.effects.map((e, i) => (
                  <li key={i} className="flex items-start gap-3 text-[15px] md:text-[16px]">
                    <span className="mt-2 w-1.5 h-1.5 rounded-full bg-[var(--gold)] shrink-0" /><span>{e}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </Section>

      {/* KÖRPERBALANCE — replaces original "Wirkung & Vorteile" title (per PDF) */}
      <Section id="koerperbalance" className="py-16 md:py-24">
        <div className="container-x text-center">
          <h2 className="display text-4xl sm:text-5xl md:text-6xl">{t.benefits.label}</h2>
          <FlowerDivider />
        </div>
        <div className="container-x grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 md:gap-5 mt-4">
          {t.benefits.items.map((b, i) => {
            const Icon = [Heart, Flower2, Sparkles, Scale][i] || Sparkles;
            return (
              <div key={b.t} className="p-6 rounded-2xl bg-[var(--cream-soft)] border border-[var(--line)] text-center" data-testid={`benefit-${i}`}>
                <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-[var(--gold)]/15 flex items-center justify-center text-[var(--gold-dark)]">
                  <Icon size={22} strokeWidth={1.5} />
                </div>
                <div className="font-serif text-xl md:text-2xl mb-2">{b.t}</div>
                <p className="text-sm text-[var(--ink-soft)]">{b.d}</p>
              </div>
            );
          })}
        </div>
      </Section>

      {/* GALLERY */}
      <Section id="gallery" className="py-16 md:py-24 bg-[var(--cream-soft)]">
        <div className="container-x text-center">
          <h2 className="display text-4xl sm:text-5xl md:text-6xl">{t.gallery.label}</h2>
          <FlowerDivider />
          <p className="text-[var(--ink-soft)]">{t.gallery.sub}</p>
        </div>
        <div className="container-x grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-4 mt-10">
          {IMAGES.gallery.map((src, i) => (
            <div key={i} className="img-mask aspect-[4/5] rounded-2xl"><img src={src} alt={`Impression ${i+1}`} /></div>
          ))}
        </div>
      </Section>

      {/* QUOTE */}
      <Section className="py-14 md:py-20">
        <div className="container-x text-center max-w-3xl mx-auto">
          <span className="block text-6xl md:text-7xl font-serif text-[var(--gold)] leading-none">“</span>
          <p className="font-serif italic text-xl md:text-3xl leading-snug mt-2">{t.quote}</p>
          <p className="mt-4 md:mt-5 text-xs md:text-sm tracking-[0.3em] uppercase text-[var(--ink-soft)]">— {t.quoteBy}</p>
        </div>
      </Section>

      {/* CONTACT */}
      <Section id="contact" className="py-16 md:py-24 bg-[var(--cream-soft)]">
        <div className="container-x text-center">
          <h2 className="display text-4xl sm:text-5xl md:text-6xl">{t.contact.label}</h2>
          <FlowerDivider />
          <p className="text-[var(--ink-soft)]">{t.contact.sub}</p>
        </div>

        <div className="container-x grid md:grid-cols-12 gap-8 md:gap-10 mt-10 md:mt-14">
          <ul className="md:col-span-5 space-y-5 text-[15px]">
            <li className="flex items-start gap-4">
              <span className="w-10 h-10 rounded-full bg-[var(--gold)]/15 flex items-center justify-center text-[var(--gold-dark)] shrink-0"><Phone size={16} /></span>
              <div>
                <div className="text-xs tracking-[0.25em] uppercase text-[var(--ink-soft)]">{t.contact.phone}</div>
                <a href={`tel:${CONTACT.phoneLink}`} className="text-base md:text-lg">{CONTACT.phone}</a>
              </div>
            </li>
            <li className="flex items-start gap-4">
              <span className="w-10 h-10 rounded-full bg-[var(--gold)]/15 flex items-center justify-center text-[var(--gold-dark)] shrink-0"><Mail size={16} /></span>
              <div>
                <div className="text-xs tracking-[0.25em] uppercase text-[var(--ink-soft)]">{t.contact.email}</div>
                <a href={`mailto:${CONTACT.email}`} className="text-base md:text-lg break-all">{CONTACT.email}</a>
              </div>
            </li>
            <li className="flex items-start gap-4">
              <span className="w-10 h-10 rounded-full bg-[var(--gold)]/15 flex items-center justify-center text-[var(--gold-dark)] shrink-0"><MapPin size={16} /></span>
              <div>
                <div className="text-xs tracking-[0.25em] uppercase text-[var(--ink-soft)]">{t.contact.address}</div>
                <div className="text-base md:text-lg">{CONTACT.street}, {CONTACT.zip} {CONTACT.city}</div>
                <div className="text-sm text-[var(--ink-soft)]">{CONTACT.country}</div>
              </div>
            </li>
            <li className="pt-3">
              <div className="text-xs tracking-[0.25em] uppercase text-[var(--ink-soft)] mb-3">{t.contact.social}</div>
              <div className="flex items-center gap-3 flex-wrap">
                <a href={SOCIAL.instagram} target="_blank" rel="noopener noreferrer" className="btn btn-ghost" data-testid="contact-instagram"><Instagram size={16} /> Instagram</a>
                <a href={SOCIAL.facebook} target="_blank" rel="noopener noreferrer" className="btn btn-ghost" data-testid="contact-facebook"><Facebook size={16} /> Facebook</a>
              </div>
            </li>
          </ul>

          <form
            action={`https://formsubmit.co/${CONTACT.email}`}
            method="POST"
            onSubmit={() => setSent(true)}
            className="md:col-span-7 bg-[var(--cream)] p-6 md:p-10 rounded-3xl border border-[var(--line)]"
            data-testid="contact-form"
          >
            <div className="font-serif text-2xl md:text-3xl mb-4 md:mb-5">{t.contact.send}</div>
            <input type="hidden" name="_subject" value="Lotus Sutra – Neue Anfrage" />
            <input type="hidden" name="_template" value="table" />
            <input type="hidden" name="_captcha" value="false" />
            <input type="hidden" name="_next" value="https://lotussutra.at/#contact" />
            <input type="text" name="_honey" style={{ display: "none" }} tabIndex={-1} autoComplete="off" />

            <input required name="name" className="field" placeholder={t.contact.namePh} />
            <input required type="email" name="email" className="field" placeholder={t.contact.emailPh} />
            <textarea required name="message" className="field" placeholder={t.contact.msgPh} rows={5} />
            <div className="flex items-center justify-between mt-5 md:mt-6 flex-wrap gap-3">
              <button className="btn btn-primary" type="submit">{t.contact.send} <ArrowRight size={14} /></button>
              {sent && <span className="text-sm text-[var(--gold-dark)]">✓ {t.contact.sent}</span>}
            </div>
            <p className="mt-3 md:mt-4 text-xs text-[var(--ink-soft)]">
              Nachrichten werden direkt an <a href={`mailto:${CONTACT.email}`} className="underline">{CONTACT.email}</a> zugestellt.
            </p>
          </form>
        </div>
      </Section>
    </main>
  );
}
